import { z } from 'zod';
import { amountMinorSchema, isoTimestampSchema, uuidSchema } from './common';

export const billLineSchema = z.object({
  id: uuidSchema,
  order_item_id: uuidSchema,
  description: z.string(),
  quantity: z.number().int(),
  unit_price_minor: amountMinorSchema,
  line_total_minor: amountMinorSchema,
});

/**
 * `platform` = collected by the delivery platform (no cash, no change); `online` = paid by the
 * guest through a payment provider (ADR-0023); `qris` / `card` = paid on the restaurant's own
 * QRIS or EDC terminal and recorded by the cashier (ADR-0050). Only `cash` enters the drawer.
 */
export const PAYMENT_METHODS = ['cash', 'platform', 'online', 'qris', 'card', 'transfer'] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];

export const paymentSchema = z.object({
  id: uuidSchema,
  method: z.enum(PAYMENT_METHODS),
  amount_minor: amountMinorSchema,
  tendered_minor: amountMinorSchema,
  change_minor: amountMinorSchema,
  /** EDC approval code / QRIS or bank transfer reference; null otherwise. */
  reference: z.string().nullable(),
  /** Staff member who took the payment; null for online payments (confirmed by the provider). */
  received_by: uuidSchema.nullable(),
  received_at: isoTimestampSchema,
});
export type Payment = z.infer<typeof paymentSchema>;

/**
 * Refundable methods: cash/QRIS/card at the counter, `online` through the payment provider.
 * Platform (delivery) payments are refunded by the platform.
 */
export const REFUND_METHODS = ['cash', 'qris', 'card', 'transfer', 'online'] as const;

/** Money given back on a payment (ADR-0051): a compensating record, payments never change. */
export const refundSchema = z.object({
  id: uuidSchema,
  payment_id: uuidSchema,
  method: z.enum(REFUND_METHODS),
  amount_minor: amountMinorSchema,
  reason: z.string(),
  /** The provider's refund id for `online` refunds. */
  provider_refund_reference: z.string().nullable(),
  /** `pending` until the provider confirms an online refund; `failed` counts nowhere. */
  status: z.enum(['pending', 'completed', 'failed']),
  /** Item refund: which bill lines and how many; empty for an amount refund. */
  lines: z.array(
    z.object({
      bill_item_id: uuidSchema,
      quantity: z.number().int(),
      amount_minor: amountMinorSchema,
    }),
  ),
  business_date: z.string(),
  refunded_by: uuidSchema,
  approved_by: uuidSchema.nullable(),
  created_at: isoTimestampSchema,
});
export type Refund = z.infer<typeof refundSchema>;

/** Gives back a guest's online payment the bill could not take (`refund_required`). */
export const refundOnlinePaymentRequestSchema = z.object({
  reason: z.string().trim().min(3).max(200),
});

/**
 * Either an amount, or bill lines with quantities (item refund): the server prices lines at what
 * the guest paid for them, service charge, tax and rounding included pro rata.
 */
export const createRefundRequestSchema = z
  .object({
    payment_id: uuidSchema,
    amount_minor: amountMinorSchema.refine((v) => v > 0, 'Must be positive').optional(),
    lines: z
      .array(z.object({ bill_item_id: uuidSchema, quantity: z.number().int().min(1).max(999) }))
      .min(1)
      .max(100)
      .optional(),
    reason: z.string().trim().min(3).max(200),
  })
  .refine((v) => (v.amount_minor === undefined) !== (v.lines === undefined), {
    message: 'Give either an amount or lines.',
    path: ['amount_minor'],
  });

export const billSchema = z.object({
  id: uuidSchema,
  bill_number: z.string(),
  /** The order's daily number: the pickup number printed on takeaway/delivery receipts. */
  order_number: z.string(),
  table_session_id: uuidSchema.nullable(),
  order_type: z.enum(['dine_in', 'takeaway', 'delivery']),
  channel: z.enum(['walk_in', 'gofood', 'grabfood', 'shopeefood', 'other']),
  external_ref: z.string().nullable(),
  customer_name: z.string().nullable(),
  order_id: uuidSchema,
  /** Dine-in only. */
  table_name: z.string().nullable(),
  status: z.enum(['open', 'settled', 'void']),
  currency: z.string(),
  lines: z.array(billLineSchema),
  subtotal_minor: amountMinorSchema,
  service_charge_minor: amountMinorSchema,
  tax_minor: amountMinorSchema,
  rounding_minor: amountMinorSchema,
  total_minor: amountMinorSchema,
  paid_minor: amountMinorSchema,
  outstanding_minor: amountMinorSchema,
  service_charge_bps: z.number().int(),
  tax_rate_bps: z.number().int(),
  payments: z.array(paymentSchema),
  refunds: z.array(refundSchema),
  /** Sum of `refunds` (the bill stays settled; net received = paid − refunded). */
  refunded_minor: amountMinorSchema,
  /**
   * Guest online payments that need staff attention: `pending` (the guest is paying right now —
   * don't collect twice) and `refund_required` (paid at the provider, but the bill could not
   * take it). Settled ones appear in `payments`.
   */
  online_payments: z.array(
    z.object({
      id: uuidSchema,
      status: z.enum(['pending', 'refund_required']),
      amount_minor: amountMinorSchema,
      created_at: isoTimestampSchema,
      failure_reason: z.string().nullable(),
    }),
  ),
  version: z.number().int(),
  created_at: isoTimestampSchema,
  settled_at: isoTimestampSchema.nullable(),
});
export type Bill = z.infer<typeof billSchema>;

export const billListResponseSchema = z.object({ items: z.array(billSchema) });

/** Creates the session's bill, or refreshes the open unpaid bill with newly submitted items. */
/** Dine-in: by table session. Takeaway/delivery: by order. Exactly one. */
export const createBillRequestSchema = z
  .object({ table_session_id: uuidSchema.optional(), order_id: uuidSchema.optional() })
  .refine((v) => (v.table_session_id === undefined) !== (v.order_id === undefined), {
    message: 'Provide either table_session_id or order_id.',
    path: ['order_id'],
  });

/** Voids an open bill nobody has paid yet; its lines are released for cancel or rebilling. */
export const voidBillRequestSchema = z.object({
  expected_version: z.number().int().nonnegative(),
  reason: z.string().trim().min(1).max(200),
});

/** QRIS / card taken on the restaurant's own terminal (ADR-0050): no change is given. */
const terminalPaymentShape = {
  /** Defaults to the outstanding balance; a smaller amount is a split payment. */
  amount_minor: amountMinorSchema.refine((v) => v > 0, 'Must be positive').optional(),
  /** EDC approval code or QRIS reference as printed on the terminal slip. */
  reference: z.string().trim().min(1).max(40).optional(),
};

export const createPaymentRequestSchema = z.discriminatedUnion('method', [
  z.object({
    bill_id: uuidSchema,
    method: z.literal('cash'),
    /** Cash handed over by the guest; change is computed by the server. */
    tendered_minor: amountMinorSchema.refine((v) => v > 0, 'Must be positive'),
  }),
  z.object({
    bill_id: uuidSchema,
    /** Delivery orders only: the platform collected the full outstanding amount. */
    method: z.literal('platform'),
  }),
  z.object({ bill_id: uuidSchema, method: z.literal('qris'), ...terminalPaymentShape }),
  z.object({ bill_id: uuidSchema, method: z.literal('card'), ...terminalPaymentShape }),
  /** Direct bank transfer to the restaurant's account, checked and recorded by the cashier. */
  z.object({ bill_id: uuidSchema, method: z.literal('transfer'), ...terminalPaymentShape }),
]);

export const paymentResultSchema = z.object({
  payment: paymentSchema,
  bill: billSchema,
});

export const cashShiftSchema = z.object({
  id: uuidSchema,
  status: z.enum(['open', 'closed']),
  user_id: uuidSchema,
  user_name: z.string(),
  currency: z.string(),
  opening_float_minor: amountMinorSchema,
  cash_sales_minor: amountMinorSchema,
  pay_in_minor: amountMinorSchema,
  pay_out_minor: amountMinorSchema,
  /** Cash given back from this drawer (ADR-0051). */
  cash_refunds_minor: amountMinorSchema,
  /** opening float + cash sales − cash refunds + pay-ins − pay-outs. */
  expected_cash_minor: amountMinorSchema,
  counted_cash_minor: amountMinorSchema.nullable(),
  /** counted − expected (negative = short). */
  discrepancy_minor: amountMinorSchema.nullable(),
  payments_count: z.number().int(),
  notes: z.string().nullable(),
  opened_at: isoTimestampSchema,
  closed_at: isoTimestampSchema.nullable(),
  version: z.number().int(),
});
export type CashShift = z.infer<typeof cashShiftSchema>;

export const currentCashShiftResponseSchema = z.object({ shift: cashShiftSchema.nullable() });

/** One money movement of a shift: a payment taken, or a refund given (ADR-0051). */
export const shiftTransactionSchema = z.object({
  id: uuidSchema,
  kind: z.enum(['payment', 'refund']),
  method: z.enum(PAYMENT_METHODS),
  /** Positive for payments, the refunded amount (positive) for refunds. */
  amount_minor: amountMinorSchema,
  reference: z.string().nullable(),
  at: isoTimestampSchema,
  bill_id: uuidSchema,
  bill_number: z.string(),
  order_id: uuidSchema,
  order_number: z.string(),
  order_type: z.enum(['dine_in', 'takeaway', 'delivery']),
  channel: z.enum(['walk_in', 'gofood', 'grabfood', 'shopeefood', 'other']),
  external_ref: z.string().nullable(),
  customer_name: z.string().nullable(),
  table_name: z.string().nullable(),
});
export type ShiftTransaction = z.infer<typeof shiftTransactionSchema>;

/** Every payment and refund linked to a shift, newest first, with totals per method. */
export const shiftTransactionsResponseSchema = z.object({
  shift_id: uuidSchema,
  items: z.array(shiftTransactionSchema),
  by_method: z.array(
    z.object({
      method: z.enum(PAYMENT_METHODS),
      received_minor: amountMinorSchema,
      refunded_minor: amountMinorSchema,
    }),
  ),
  /** Received − refunded, all methods. */
  net_minor: amountMinorSchema,
  payments_count: z.number().int(),
  /**
   * Menu items on the bills paid in this shift (each bill once), item refunds deducted: what
   * the shift sold. Amounts are line totals before service charge and tax.
   */
  items_sold: z.array(
    z.object({ name: z.string(), quantity: z.number().int(), amount_minor: amountMinorSchema }),
  ),
  /** Money received per table (dine-in) or per takeaway/delivery channel, refunds deducted. */
  by_table: z.array(
    z.object({
      order_type: z.enum(['dine_in', 'takeaway', 'delivery']),
      channel: z.enum(['walk_in', 'gofood', 'grabfood', 'shopeefood', 'other']),
      table_name: z.string().nullable(),
      bills: z.number().int(),
      net_minor: amountMinorSchema,
    }),
  ),
});
export type ShiftTransactions = z.infer<typeof shiftTransactionsResponseSchema>;
export const cashShiftListResponseSchema = z.object({ items: z.array(cashShiftSchema) });

export const openCashShiftRequestSchema = z.object({
  opening_float_minor: amountMinorSchema.refine((v) => v >= 0, 'Cannot be negative'),
});

export const cashMovementRequestSchema = z.object({
  type: z.enum(['pay_in', 'pay_out']),
  amount_minor: amountMinorSchema.refine((v) => v > 0, 'Must be positive'),
  reason: z.string().trim().min(1).max(200),
});

export const closeCashShiftRequestSchema = z.object({
  expected_version: z.number().int().nonnegative(),
  counted_cash_minor: amountMinorSchema.refine((v) => v >= 0, 'Cannot be negative'),
  notes: z.string().trim().max(500).nullable().default(null),
});
